import { useQueryClient } from '@tanstack/react-query'

import { conRespaldo } from '@/lib/campo/loUltimoQueSeSupo'
import { supabase } from '@/lib/supabase/cliente'

import { useEffect, useState } from 'react'

import { iniciarRastreoAndroid } from '@/lib/nativo/rastreoAndroid'
import { iniciarPushNativo } from '@/lib/nativo/pushNativo'
import { IconoCotizacion } from '@/shared/ui/Iconos/Iconos'
import { Cotizador } from '@/modules/cotizador/Cotizador'
import { FormularioNuevaOrden } from '@/modules/ordenes-servicio/FormularioNuevaOrden'
import { FotosOrden } from '@/modules/ordenes-servicio/FotosOrden'
import { useFotosDeLaOrden } from '@/modules/ordenes-servicio/useFotosOrden'
import { Boton } from '@/shared/ui/Boton/Boton'
import { Modal } from '@/shared/ui/Modal/Modal'
import { useSesion } from '@/modules/auth/useSesion'
import { MiDia } from './MiDia'
import { InterruptorPush } from '@/modules/avisos/InterruptorPush'
import { AvisoBateria } from '@/modules/avisos/AvisoBateria'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { ListaDeSolicitudes } from './ListaDeSolicitudes'
import { LoQueLevante, type Levantada } from './LoQueLevante'
import { MiRuta } from './MiRuta'
import { TarjetaDeCampo } from './TarjetaDeCampo'
import { EnEsteTelefono } from './EnEsteTelefono'
import { MisHerramientas } from './MisHerramientas'
import { MisRecogidas } from './MisRecogidas'
import { useMiTrabajo } from './useMiTrabajo'
import { useMisHerramientas } from './useMisHerramientas'
import { useMisSolicitudes } from './useMisSolicitudes'
import { useColaDeFotos } from './useColaDeFotos'
import { PorCotizar } from './PorCotizar'
import { useColaDeAcciones } from './useColaDeAcciones'
import { useDescargarCatalogo } from '@/modules/cotizador/useCotizador'
import estilos from './PaginaMiTrabajo.module.css'

/**
 * Mi trabajo.
 *
 * La pantalla del operario. No es la matriz en pequeño: es lo único que él
 * necesita, en el orden en que lo necesita, con los dedos y de pie.
 *
 * LO QUE NO TIENE, A PROPÓSITO
 * Ni valor de la cotización, ni bitácora, ni las órdenes de sus compañeros, ni
 * la ficha del documento. Antes los botones de campo vivían dentro del detalle
 * de la orden de servicio: había que bajar mil cien píxeles de datos de
 * oficina —incluido el precio del trabajo— para llegar a «Llegué», y encima el
 * rol del operario ni siquiera podía abrir esa pantalla.
 *
 * LO QUE SÍ TIENE
 * Dónde es y cómo llegar, cuánto le queda, y el botón que corresponde al
 * momento en que está. Nada más.
 */
export function PaginaMiTrabajo() {
  const { perfil } = useSesion()
  const { trabajos, cargando, error } = useMiTrabajo()
  const { solicitudes } = useMisSolicitudes()
  const { herramientas } = useMisHerramientas()
  // Los marcajes que se hicieron sin señal y todavía no han salido del
  // teléfono. Se intentan al abrir y cuando vuelve la conexión.
  const { pendientes: marcajesEnEspera } = useColaDeAcciones()
  /*
   * El catálogo y los clientes, descargados desde que entra.
   *
   * Estaban enganchados al cotizador, así que solo viajaban si el técnico lo
   * abría con señal. En la obra eso llega tarde: se vio al probar levantar
   * una orden sin cobertura —«Falta el cliente»— porque la lista nunca se
   * había traído. Aquí se piden al abrir la aplicación, que es cuando
   * todavía suele haber señal.
   */
  useDescargarCatalogo()

  // En la app nativa de Android arranca el servicio en segundo plano que reporta
  // la ubicación aunque el teléfono esté bloqueado. En la web no hace nada: la
  // guarda de isNativePlatform está dentro de iniciarRastreoAndroid.
  /*
   * SE TRAE LO DE LAS OTRAS PANTALLAS MIENTRAS HAY SEÑAL.
   *
   * «Mi programación» y «Mi historial» guardan lo último que vieron para
   * poder abrirse en la obra, pero eso solo sirve si alguna vez se vieron. En
   * el barrido de botones las dos fallaban sin señal por eso: el técnico
   * entra por «Mi trabajo» y puede no abrir las otras hasta estar ya sin
   * cobertura. Se piden aquí, calladas, para que el respaldo exista.
   */
  const clienteConsultas = useQueryClient()

  useEffect(() => {
    if (!navigator.onLine) return

    void clienteConsultas.prefetchQuery({
      queryKey: ['mi-programacion'],
      queryFn: () =>
        conRespaldo('mi-programacion', async () => {
          // Si falla, que falle: devolver [] guardaba una lista vacía encima
          // de la buena, y sin señal la programación salía en blanco.
          const { data, error } = await supabase.rpc('fn_mi_programacion')
          if (error) throw error
          return data ?? []
        }),
    })
    void clienteConsultas.prefetchQuery({
      queryKey: ['mi-historial'],
      queryFn: () =>
        conRespaldo('mi-historial', async () => {
          const { data, error } = await supabase.rpc('fn_mi_historial')
          if (error) throw error
          return data ?? []
        }),
    })
  }, [clienteConsultas])

  useEffect(() => {
    void iniciarRastreoAndroid()
    // El push nativo: pide el permiso de notificaciones y registra el token FCM
    // de este teléfono. Así el aviso llega aunque la app esté cerrada. En la web
    // no hace nada (la guarda de isNativePlatform está dentro).
    void iniciarPushNativo()
  }, [])

  /**
   * LEVANTAR UN TRABAJO DESDE LA CALLE.
   *
   * Es el momento en que más barato sale vender: el técnico está en la casa,
   * ya tiene la confianza del cliente, y el cliente le señala la otra pared.
   * Hasta hoy eso se perdía, o viajaba por WhatsApp hasta que alguien en la
   * oficina se acordara.
   *
   * Van encadenados a propósito —se registra el trabajo y se sigue derecho a
   * cotizarlo— porque son un solo gesto para quien está allí parado: si al
   * terminar de registrarlo hubiera que buscarlo en una lista para ponerle
   * precio, la mitad se quedarían sin cotizar.
   */
  const [levantando, setLevantando] = useState(false)
  const [cotizando, setCotizando] = useState<{
    id: string
    numero: string
    cliente: string
    direccion: string | null
  } | null>(null)
  /**
   * EL PASO 3: LAS FOTOS, AL FINAL.
   *
   * Estaban en el formulario, y ahí se piden demasiado pronto: el técnico
   * todavía no ha terminado de mirar la instalación y no sabe qué va a hacer
   * falta retratar. Al final de la cotización sí lo sabe —acaba de decidir qué
   * lleva el trabajo— y encima las fotos respaldan el precio que acaba de
   * poner, que es para lo que sirven cuando el cliente lo discute.
   */
  const [retratando, setRetratando] = useState<{
    id: string
    numero: string
    cliente: string
    /*
     * LA DIRECCIÓN DEL TRABAJO, PARA EL SELLO DE LA FOTO.
     *
     * El sello escribe la dirección que Google devuelve al revés desde el GPS,
     * y cuando Google no contesta —sin señal, o la llave no autoriza el origen
     * de la APK— cae a los grados: «4°44'3.341"N 75°54'48.714"W». Eso prueba
     * el punto, pero nadie lo lee. La dirección del trabajo ya la escribió el
     * técnico al levantarlo: llevarla aquí es gratis y la foto nunca se queda
     * sin sitio legible.
     */
    direccion: string | null
  } | null>(null)

  const nombre = perfil?.nombre?.split(' ')[0] ?? ''

  /**
   * Retomar algo que quedó a medias. Cada paso vuelve a abrir el suyo: la
   * cotización si falta el precio, las fotos si el precio ya está.
   */
  function retomar(l: Levantada) {
    const orden = {
      id: l.orden_servicio_id,
      numero: l.numero,
      cliente: l.cliente,
      direccion: l.direccion,
    }
    // 'cotizada' es la única que va a fotos: tiene precio y le falta el
    // retrato. 'rechazada' y las de precio a medias vuelven al cotizador.
    if (l.paso === 'cotizada') setRetratando(orden)
    else setCotizando(orden)
  }

  const levantar = (
    <>
      <button type="button" className={estilos.levantar} onClick={() => setLevantando(true)}>
        <IconoCotizacion />
        Cotizar un trabajo nuevo
      </button>

      <FormularioNuevaOrden
        abierto={levantando}
        onCerrar={() => setLevantando(false)}
        onCreada={(orden) => {
          setLevantando(false)
          setCotizando(orden)
        }}
      />

      {cotizando && (
        <Cotizador
          abierto
          ordenServicioId={cotizando.id}
          ordenNumero={cotizando.numero}
          clienteNombre={cotizando.cliente}
          enLaCalle
          onCerrar={() => {
            const orden = cotizando
            setCotizando(null)
            setRetratando(orden)
          }}
        />
      )}

      {retratando && (
        <Modal
          abierto
          titulo="Paso 3 de 3 · Las fotos"
          subtitulo={`${retratando.numero} · ${retratando.cliente}`}
          onCerrar={() => setRetratando(null)}
          pie={
            <PieDelPasoDeFotos
              ordenServicioId={retratando.id}
              onTerminar={() => setRetratando(null)}
            />
          }
        >
          <FotosOrden
            ordenServicioId={retratando.id}
            autoAbrir
            sello={{ referencia: retratando.numero, direccion: retratando.direccion }}
          />
        </Modal>
      )}
    </>
  )

  if (cargando) return <Cargando mensaje="CARGANDO TRABAJO ACTIVO..." />

  if (error) return <Aviso tipo="error">{error}</Aviso>

  if (trabajos.length === 0 && solicitudes.length === 0) {
    return (
      <div className={estilos.pagina}>
        {/* El panel lo explica en su propio lenguaje; no hace falta un vacío
            aparte que diga lo mismo con otras palabras. */}
        <MiDia trabajos={[]} nombre={nombre} />

        {/* Sin trabajo de obra puede haber encargos de cotizar: es
            precisamente el día en que se sale a mirar. */}
        <PorCotizar />

        {/* Sin trabajo asignado también se puede levantar uno: el técnico pasa
            por una obra, ve algo y lo registra. */}
        {levantar}

        <LoQueLevante onSeguir={retomar} />

        {/* Sin trabajo hoy también puede tener cosas guardadas sin señal y
            herramienta encima —esa es justo la que debería volver—. Antes
            esta pantalla no las mostraba: Luis Alfonso, sin trabajo un día,
            no tenía cómo ver ni devolver las dos que llevaba. */}
        <EnEsteTelefono pendientes={marcajesEnEspera} />
        <MisRecogidas />
        <MisHerramientas herramientas={herramientas} />
      </div>
    )
  }

  const abiertas = trabajos.filter((t) => t.estado !== 'cerrada' && t.estado !== 'finalizada')
  const cerradas = trabajos.filter((t) => t.estado === 'cerrada' || t.estado === 'finalizada')

  return (
    <div className={estilos.pagina}>
      {/* Arriba del trabajo mientras esté apagado: es lo único de esta
          pantalla que hay que hacer una sola vez, y si no lo hace, nada de lo
          que se construyó detrás le sirve. Encendido se encoge a una línea. */}
      {/* LO PRIMERO, Y LO ÚNICO OSCURO. Ancla la pantalla y contesta lo que
          se pregunta a las siete de la mañana: cuánto hay hoy, dónde empieza,
          qué está pasando ahora. */}
      <MiDia trabajos={trabajos} nombre={nombre} />

      {/* LO QUE LE ENCARGARON COTIZAR. Va antes del trabajo del día porque
          suele ser lo que se hace de camino: pasar, mirar y poner precio. */}
      <PorCotizar />

      {/* Lo que hizo sin señal y espera, y lo que la base no aceptó. */}
      <EnEsteTelefono pendientes={marcajesEnEspera} />

      <InterruptorPush />

      {levantar}

      {/* Lo suyo sin terminar, antes que el trabajo asignado: lo asignado ya
          tiene quien se lo recuerde —está programado—; esto solo lo sabe él. */}
      <LoQueLevante onSeguir={retomar} />

      {/* Solo asoma en la app de Android si sigue dentro del ahorro de batería;
          concedida la exención, desaparece. */}
      <AvisoBateria />

      {/* La ruta del día en el mapa. Se pliega sola y solo aparece con dos o más
          paradas: el técnico pidió ver por dónde le toca andar. */}
      <MiRuta trabajos={trabajos} />

      {abiertas.map((trabajo) => (
        <TarjetaDeCampo key={trabajo.orden_trabajo_id} trabajo={trabajo} />
      ))}

      {/* Lo que pidió y en qué va. Debajo del trabajo y no en otra pantalla:
          es una sola pantalla, y esto es lo segundo que mira. */}
      <ListaDeSolicitudes solicitudes={solicitudes} />

      {/* Lo que lleva encima. Debajo de lo que tiene que hacer, porque no es
          una tarea: es lo que hay que acordarse de devolver. Y sin botón de
          devolver a propósito —la herramienta vuelve cuando alguien en bodega
          la tiene en la mano, no cuando alguien lo dice desde la calle. */}
      {/* Lo que le toca ir a recoger (operario, 0239). */}
      <MisRecogidas />

      <MisHerramientas herramientas={herramientas} />

      {/* Lo que ya cerró hoy. Se queda a la vista porque acabar el día con la
          pantalla vacía no dice «terminaste», dice «se perdió algo». */}
      {cerradas.length > 0 && (
        <section className={estilos.hechas}>
          <p className={estilos.hechasTitulo}>
            {cerradas.length === 1 ? 'Terminada hoy' : `Terminadas hoy · ${cerradas.length}`}
          </p>

          {cerradas.map((trabajo) => (
            <div key={trabajo.orden_trabajo_id} className={estilos.hecha}>
              <span className={estilos.hechaNumero}>{trabajo.numero}</span>
              <span className={estilos.hechaCliente}>{trabajo.cliente ?? 'Sin cliente'}</span>
              <span className={estilos.hechaHoras}>
                {trabajo.horas_ejecutadas + trabajo.horas_extra} h
              </span>
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

/**
 * EL PIE DEL PASO 3: NO SE TERMINA SIN LAS FOTOS.
 *
 * «Terminar» solo cierra cuando ya hay al menos cinco fotos —la misma prueba
 * que exige el cierre de la obra en campo (TarjetaDeCampo)—. Antes cerraba
 * siempre, así que una orden podía nacer sin una sola foto que respaldara el
 * precio, que es justo lo que el cliente discute después.
 *
 * Vive en su propio componente porque el conteo sale de un hook
 * (`useFotosDeLaOrden`) y los hooks no pueden ir dentro del `retratando && (…)`
 * condicional de arriba.
 */
function PieDelPasoDeFotos({
  ordenServicioId,
  onTerminar,
}: {
  ordenServicioId: string
  onTerminar: () => void
}) {
  const { data: fotos = [] } = useFotosDeLaOrden(ordenServicioId)
  // Las que están en el teléfono esperando subir cuentan igual: el técnico ya
  // las tomó, y exigir señal para poder terminar es justo lo que se quería
  // evitar. Suben solas después.
  const { enCola } = useColaDeFotos(ordenServicioId)
  // La misma vara que el cierre en campo: cinco fotos.
  const MIN_FOTOS = 5
  const faltan = Math.max(0, MIN_FOTOS - (fotos.length + enCola.length))

  return (
    <div className={estilos.pieFotos}>
      {faltan > 0 && (
        <span className={estilos.faltanFotos}>
          {faltan === 1 ? 'Falta 1 foto' : `Faltan ${faltan} fotos`}
        </span>
      )}
      <Boton variante="principal" onClick={onTerminar} disabled={faltan > 0}>
        Terminar
      </Boton>
    </div>
  )
}
